import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Loader2, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScoreRadial } from "@/components/ScoreRadial";
import { useLang } from "@/i18n/LanguageContext";

/** First read of an idea: title, provisional score, signal, first step + "Next step" CTA. Used for guests and logged-in users. */
export function IdeaPreview({ name, snapshot, onNext, loading, testid = "flow-preview" }) {
  const { t } = useLang();
  if (!snapshot) return null;
  const uplift = Number(snapshot.next_uplift_pct) || (snapshot.first_score != null ? Math.max(6, Math.min(25, Math.round((100 - snapshot.first_score) * 0.18))) : 0);
  return (
    <motion.div key="preview" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }} className="w-full max-w-2xl mx-auto text-center" data-testid={testid}>
      <span className="cpenz-overline">{t("home.captured")}</span>
      <h1 className="mt-3 cpenz-display text-3xl sm:text-4xl text-[#0b0f1a]" data-testid="preview-title">{name}</h1>
      {snapshot.one_liner && <p className="mt-3 text-slate-500 leading-relaxed">{snapshot.one_liner}</p>}
      {snapshot.first_score != null && (
        <div className="mt-8 flex flex-col items-center" data-testid="preview-score">
          <ScoreRadial total={snapshot.first_score} band="" size={150} />
          <p className="mt-1 text-[11px] font-semibold tracking-wider uppercase text-slate-400">{t("home.first_score")}</p>
        </div>
      )}
      {snapshot.signal && <p className="mt-6 text-[#0b0f1a] leading-relaxed max-w-lg mx-auto" data-testid="preview-signal"><Sparkles size={14} className="inline mr-1.5 text-[#7c3aed]" />{snapshot.signal}</p>}
      {snapshot.first_step && (
        <div className="mt-6 rounded-2xl bg-[#f4f6fa] p-5 text-left">
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#7c3aed]">{t("home.first_step")}</p>
          <p className="mt-1 text-sm text-slate-600" data-testid="preview-first-step">{snapshot.first_step}</p>
        </div>
      )}
      {uplift > 0 && (
        <div className="mt-8 mx-auto inline-flex max-w-md flex-col items-center gap-1 rounded-2xl bg-[#0b0f1a] px-6 py-4 text-white" data-testid="preview-uplift">
          <span className="inline-flex items-center gap-2 text-2xl font-bold tracking-tight tabular-nums"><TrendingUp size={20} className="text-[#a78bfa]" />+{uplift}%</span>
          <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#c4b5fd]">{t("home.uplift_label")}</span>
          {snapshot.uplift_why && <span className="mt-1 text-xs text-white/70 leading-snug" data-testid="preview-uplift-why">{snapshot.uplift_why}</span>}
        </div>
      )}
      <div className="mt-5 flex justify-center">
        <Button data-testid="preview-continue-btn" onClick={onNext} disabled={loading} className="rounded-full bg-[#0b0f1a] text-white px-7 h-12 text-base">
          {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> {t("flow.analyzing")}</> : <>{t("flow.next_step")} <ArrowRight size={16} className="ml-1" /></>}
        </Button>
      </div>
      <p className="mt-4 text-xs text-slate-400">{t("home.explore_hint")}</p>
    </motion.div>
  );
}
